import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Navbar, 
  Nav, 
  Container, 
  Dropdown, 
  Badge, 
  Button,
  Offcanvas
} from 'react-bootstrap';
import axios from '../../services/api-axios';
import { setUsername, reset as resetUsers } from '../../store/onlineUsersSlice';
import { reset as resetLobby } from '../../store/lobbySlice';
import { resetGameState as resetGame } from '../../store/gameSlice';
import { reset } from '../../services/pubsub';
import styles from './Header.module.css';

function Header() {
  const dispatch = useDispatch();
  const username = useSelector((state) => state.onlineUsers.username);
  const lobbyId = useSelector((state) => state.lobby.lobbyId);
  const lobbyName = useSelector((state) => state.lobby.lobbyName);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(3);
  const [showOffcanvas, setShowOffcanvas] = useState(false);

  useEffect(() => {
    axios.get('/api/user/info')
      .then((res) => {
        dispatch(setUsername({ username: res.data.email }));
      })
      .catch((err) => {
        console.log('ERROR IN HEADER !!');
      });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('SavedToken');
    dispatch(resetUsers());
    dispatch(resetLobby());
    dispatch(resetGame());
    reset();
    navigate('/login');
    setShowOffcanvas(false);
  };

  const handleProfileClick = () => {
    console.log('Profile clicked');
    setShowOffcanvas(false);
  };

  const handleSettingsClick = () => {
    console.log('Settings clicked');
    setShowOffcanvas(false);
  };

  const handleNotificationsClick = () => {
    setNotifications(0);
    console.log('Notifications clicked');
  };

  const handleNavClick = () => {
    setShowOffcanvas(false);
  };

  return (
    <>
      <Navbar expand="lg" className={`${styles.customNavbar} sticky-top`}>
        <Container fluid className="px-3 px-lg-4">
          {/* Brand */}
          <Navbar.Brand as={Link} to="/" className={styles.navbarBrand}>
            <div className={styles.brandIcon}>🎮</div>
            <div className={styles.brandText}>ZNK</div>
          </Navbar.Brand>

          {/* Center Status - Only show on medium screens and up */}
          {lobbyId && (
            <div className={`${styles.lobbyStatus} d-none d-md-flex mx-auto`}>
              <div className={styles.lobbyPulse} />
              <span className="d-none d-lg-inline">In Lobby: </span>
              <span className="d-lg-none">Lobby </span>
              {lobbyName || lobbyId}
            </div>
          )}

          {/* Desktop Navigation and User Actions */}
          <div className="d-none d-lg-flex align-items-center gap-3">
            {/* Desktop Navigation */}
            <Nav className="me-3">
              <Nav.Link as={Link} to="/" className={styles.navLink}>
                🏠 Home
              </Nav.Link>
              <Nav.Link as={Link} to="/home/leaderboard" className={styles.navLink}>
                🏆 Leaderboard
              </Nav.Link>
              <Nav.Link as={Link} to="/home/tournaments" className={styles.navLink}>
                🎯 Tournaments
              </Nav.Link>
              <Nav.Link as={Link} to="/home/tutorial" className={styles.navLink}>
                🎓 Tutorial
              </Nav.Link>
            </Nav>

            {/* Notifications */}
            <Button
              onClick={handleNotificationsClick}
              className={styles.notificationBtn}
            >
              🔔
              {notifications > 0 && (
                <div className={styles.notificationBadge}>
                  {notifications}
                </div>
              )}
            </Button>

            {/* User Dropdown */}
            <Dropdown align="end">
              <Dropdown.Toggle className={styles.dropdownToggle}>
                <div className={styles.userAvatar}>
                  {username ? username.charAt(0).toUpperCase() : '?'}
                </div>
                <span className="d-none d-xl-inline">{username || 'Guest'}</span>
              </Dropdown.Toggle>

              <Dropdown.Menu className={styles.dropdownMenu}>
                <div className={styles.dropdownHeader}>
                  <div>{username || 'Guest User'}</div>
                  <div className={styles.userStatus}>Online • Ready to play</div>
                </div>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleProfileClick} className={styles.dropdownItem}>
                  👤 Profile
                </Dropdown.Item>
                <Dropdown.Item onClick={handleSettingsClick} className={styles.dropdownItem}>
                  ⚙️ Settings
                </Dropdown.Item>
                <Dropdown.Item 
                  onClick={handleLogout} 
                  className={`${styles.dropdownItem} ${styles.dropdownItemLogout}`}
                >
                  🚪 Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* Mobile Actions */}
          <div className="d-flex d-lg-none align-items-center gap-2">
            {/* Mobile Notifications */}
            <Button
              onClick={handleNotificationsClick}
              className={`${styles.notificationBtn} d-none d-sm-block`}
            >
              🔔
              {notifications > 0 && (
                <div className={styles.notificationBadge}>
                  {notifications}
                </div>
              )}
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="outline-light"
              onClick={() => setShowOffcanvas(true)}
              className={styles.notificationBtn}
            >
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </Button>
          </div>
        </Container>
      </Navbar>

      {/* Mobile Offcanvas Menu */}
      <Offcanvas
        show={showOffcanvas}
        onHide={() => setShowOffcanvas(false)}
        placement="end"
        className={styles.offcanvas}
      >
        <Offcanvas.Header closeButton className={styles.offcanvasHeader}>
          <Offcanvas.Title className={styles.offcanvasTitle}>Menu</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {/* Mobile User Section */}
          <div className={styles.mobileUserSection}>
            <div className={styles.mobileUserInfo}>
              <div className={styles.userAvatar}>
                {username ? username.charAt(0).toUpperCase() : '?'}
              </div>
              <div>
                <div className={styles.mobileUserName}>{username || 'Guest'}</div>
                <div className={styles.mobileUserStatus}>Online • Ready to play</div>
              </div>
            </div>

            <Button onClick={handleProfileClick} className={styles.mobileActionBtn}>
              👤 Profile
            </Button>
            <Button onClick={handleSettingsClick} className={styles.mobileActionBtn}>
              ⚙️ Settings
            </Button>
            <Button onClick={handleNotificationsClick} className={`${styles.mobileActionBtn} d-sm-none`}>
              🔔 Notifications {notifications > 0 && `(${notifications})`}
            </Button>
          </div>

          {/* Mobile Lobby Status */}
          {lobbyId && (
            <div className={`${styles.lobbyStatus} mb-3 d-md-none`}>
              <div className={styles.lobbyPulse} />
              <span>In Lobby: {lobbyId}</span>
            </div>
          )}

          {/* Mobile Navigation */}
          <Nav className="flex-column">
            <Nav.Link as={Link} to="/" className={styles.navLink} onClick={handleNavClick}>
              🏠 Home
            </Nav.Link>
            <Nav.Link as={Link} to="/home/leaderboard" className={styles.navLink} onClick={handleNavClick}>
              🏆 Leaderboard
            </Nav.Link>
            <Nav.Link as={Link} to="/tournaments" className={styles.navLink} onClick={handleNavClick}>
              🎯 Tournaments
            </Nav.Link>           
            <Nav.Link as={Link} to="/home/tutorial" className={styles.navLink} onClick={handleNavClick}>
              🎓 Tutorial
            </Nav.Link>
          </Nav>

          {/* Mobile Logout */}
          <div className="mt-auto pt-3">
            <Button onClick={handleLogout} className={`${styles.mobileActionBtn} ${styles.mobileLogoutBtn}`}>
              🚪 Logout
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export default Header;